import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { AppState } from '@/store/store';
import { httpGet } from '@/utils/helpers/httpHelper';
import CONFIG from 'config';

export type CampaignRequest = {
  slug: string;
  name: string;
  price: string;
  discountedPrice: string;
  subCategoryName: string;
  images: ImageType[];
  companyName: string;
  status: any;
  createdAt: string;
  partnerLogo: string;
  partnerName: string;
  partnerPhone: string;
  campaignStartDate: string;
  campaignEndDate: string;
};

export type StatusAndReason = {
  text: string;
  value: string;
};

export type RequestStatusesType = {
  statuses: StatusAndReason[];
  reasons: StatusAndReason[];
};

export type ImageType = {
  uploadedImagePath: string;
  order: number;
};

export const fetchStatuses: any = createAsyncThunk(
  'requests/fetchStatuses',
  async () => {
    const { data } = await httpGet(`${CONFIG.ENDPOINTS.statuses}`);
    return data;
  }
);

export const fetchCampaignRequests: any = createAsyncThunk(
  'campaignRequests/fetchCampaignRequests',
  async (arg, { getState }) => {
    const { campaignRequest }: any = getState();

    const { data } = await httpGet(
      campaignRequest.filter !== 'all'
        ? `${CONFIG.ENDPOINTS.campaignRequests}?page=${campaignRequest.page}&limit=${campaignRequest.numPerPage}&status=${campaignRequest.filter}`
        : `${CONFIG.ENDPOINTS.campaignRequests}?page=${campaignRequest.page}&limit=${campaignRequest.numPerPage}`
    );
    return data;
  }
);

const campaignRequestAdapter = createEntityAdapter<CampaignRequest>({
  selectId: (campaignRequest) =>
    `${campaignRequest.createdAt}/${Math.random()}`,
});

export const campaignRequestSlice = createSlice({
  name: 'campaignRequests',
  initialState: campaignRequestAdapter.getInitialState({
    loading: true,
    error: false,
    page: 1,
    numPerPage: 25,
    sort: 'createdAt',
    direction: 'desc',
    totalCount: null,
    filter: 'all',
    campaignRequests: null as CampaignRequest[],
    requestStatuses: null as RequestStatusesType,
  }),
  reducers: {
    changeFilter: (state, action) => {
      state.filter = action.payload;
    },
    changePage: (state, action) => {
      state.page = action.payload;
    },
    changeNumPerPage: (state, action) => {
      state.numPerPage = action.payload;
    },
    changeSort: (state, action) => {
      state.sort =
        action.payload.direction === 'none'
          ? ''
          : `${action.payload.name}_${action.payload.direction}`;
    },
    changeAllFilters: (state, action) => {
      state.sort = action.payload.sort;
      state.page = action.payload.page;
      state.numPerPage = action.payload.perPage;
    },
  },
  extraReducers: {
    [fetchCampaignRequests.pending](state) {
      state.loading = true;
    },
    [fetchCampaignRequests.fulfilled](state, { payload }) {
      state.loading = false;
      campaignRequestAdapter.setAll(state, payload.data.items);
      state.totalCount = payload?.data.totalCount;
    },
    [fetchCampaignRequests.rejected](state) {
      state.loading = false;
      state.error = true;
    },
    [fetchStatuses.pending](state) {
      state.loading = true;
    },
    [fetchStatuses.fulfilled](state, { payload }) {
      state.requestStatuses = payload.data;
      state.loading = false;
    },
    [fetchStatuses.rejected](state) {
      state.loading = false;
      state.error = true;
    },
  },
});

export const {
  changePage,
  changeNumPerPage,
  changeSort,
  changeAllFilters,
  changeFilter,
} = campaignRequestSlice.actions;
export const campaignRequestSelectors =
  campaignRequestAdapter.getSelectors<AppState>(
    (state) => state.campaignRequest
  );
campaignRequestAdapter.getSelectors<AppState>((state) => state.campaignRequest);

// export const { selectAll, selectById, selectIds, selectEntities } = companySelectors;

export default campaignRequestSlice.reducer;
